import { Gift, Trophy, Coffee, Sparkles, Ticket, Users, Heart, Share2, Megaphone, Star, MessageSquareReply, ShieldCheck, Cake, Crown } from "lucide-react";

/**
 * Phase 25 — Three new sales-page blocks:
 *   1) LoyaltyVouchersBlock — punch cards, points, tiers, voucher engine
 *   2) CommunityBlock      — cooperative marketing between local tenants
 *   3) ReviewsBlock        — review control + auto-respond + social re-share
 *
 * Designed to live between <CuisineShowcase/> and <Pricing/>.
 */

export function LoyaltyVouchersBlock() {
  return (
    <section className="sp-section sp-section--loyalty" id="loyalty" data-testid="sales-loyalty-block">
      <div className="sp-container">
        <div className="sp-eyebrow sp-eyebrow--amber">
          <Trophy size={14} /> Built-in loyalty engine
        </div>
        <h2 className="sp-h2">
          Turn one-time diners into <em>regulars who bring friends</em>.
        </h2>
        <p className="sp-lede" style={{ maxWidth: 720 }}>
          The loyalty system is on by default for every business on HustlePlate —
          no extra subscription, no add-on fees. Points, punch cards, gift
          vouchers, BOGO deals — all running on the same wallet your customers
          already have.
        </p>

        <div className="sp-loyalty-grid">
          <div className="sp-loyalty-card">
            <div className="sp-loyalty-card__icon"><Trophy size={20} /></div>
            <h3>Points + Tiers</h3>
            <p>
              Earn 1 point per $1 spent, Bronze through VIP, with up to <b>2× earning multiplier</b> for top customers. Auto-credited on every order. Auto-redeemed at checkout.
            </p>
          </div>
          <div className="sp-loyalty-card">
            <div className="sp-loyalty-card__icon"><Coffee size={20} /></div>
            <h3>Punch cards</h3>
            <p>
              The classic that still converts: <b>Buy 5 coffees, get 1 free</b>. Or 10 pizzas, or 8 schnitzels — you pick the item, we stamp the card.
            </p>
          </div>
          <div className="sp-loyalty-card">
            <div className="sp-loyalty-card__icon"><Ticket size={20} /></div>
            <h3>Vouchers + coupons</h3>
            <p>
              Generate single codes, bulk gift cards, or birthday surprises. Percent-off, dollar-off, free-item — applied at checkout in one click.
            </p>
          </div>
          <div className="sp-loyalty-card">
            <div className="sp-loyalty-card__icon sp-loyalty-card__icon--pink"><Cake size={20} /></div>
            <h3>Birthday Club marketing</h3>
            <p>
              Capture a date at sign-up and we run the rest. <b>One week before</b> they get a "save the date" SMS, on the day a free dessert voucher, the morning after a thank-you with their booking-ready link. <b>~38% redemption</b> — birthdays are the easiest sale you'll ever make.
            </p>
          </div>
          <div className="sp-loyalty-card">
            <div className="sp-loyalty-card__icon sp-loyalty-card__icon--purple"><Crown size={20} /></div>
            <h3>VIP Club marketing</h3>
            <p>
              Your top 5% — auto-flagged once they cross the VIP threshold. They unlock <b>chef's-table invites, soft-launch previews, "menu-before-it-launches" tastings</b>, and a private booking line. Drives outsized word-of-mouth from the customers who already love you most.
            </p>
          </div>
          <div className="sp-loyalty-card">
            <div className="sp-loyalty-card__icon"><Sparkles size={20} /></div>
            <h3>Auto-rewards</h3>
            <p>
              Sign-up bonus, anniversary perk, win-back coupons for lapsed customers, "we miss you" texts after 60 days quiet — every loop triggered automatically, no spreadsheets.
            </p>
          </div>
        </div>

        <div className="sp-stat-strip" data-testid="sales-loyalty-stats">
          <div className="sp-stat-strip__item">
            <div className="sp-stat-strip__num">23%</div>
            <div className="sp-stat-strip__label">average repeat-visit lift on businesses running loyalty</div>
          </div>
          <div className="sp-stat-strip__item">
            <div className="sp-stat-strip__num">4.2×</div>
            <div className="sp-stat-strip__label">customer lifetime value vs non-members</div>
          </div>
          <div className="sp-stat-strip__item">
            <div className="sp-stat-strip__num">$0</div>
            <div className="sp-stat-strip__label">extra cost — included on every HustlePlate plan</div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function CommunityBlock() {
  return (
    <section className="sp-section sp-section--community" id="community" data-testid="sales-community-block">
      <div className="sp-container">
        <div className="sp-eyebrow sp-eyebrow--rose">
          <Users size={14} /> Cooperative marketing
        </div>
        <h2 className="sp-h2">
          A community of local businesses, <em>marketing for each other</em>.
        </h2>
        <p className="sp-lede" style={{ maxWidth: 720 }}>
          HustlePlate is built on the radical idea that a kebab shop, a bakery,
          a wine bar and a barber two doors down don't compete — they win
          together. We make co-marketing automatic.
        </p>

        <div className="sp-community-grid">
          <article className="sp-community-card">
            <div className="sp-community-card__icon"><Share2 size={20} /></div>
            <h3>Cross-feature each other</h3>
            <p>
              The Recipe Gift PDF a diner downloads from your kebab shop has a
              little <em>"You'll love…"</em> page at the back featuring the
              cafe up the road. The cafe's PDF has you. Both of you ship
              traffic to each other for free.
            </p>
          </article>
          <article className="sp-community-card">
            <div className="sp-community-card__icon"><Heart size={20} /></div>
            <h3>Shared loyalty wallet</h3>
            <p>
              Diners earn points at one HustlePlate business, spend them at
              another. The whole high street becomes a loyalty program. You
              sell more. They sell more.
            </p>
          </article>
          <article className="sp-community-card">
            <div className="sp-community-card__icon"><Megaphone size={20} /></div>
            <h3>Neighbourhood directory</h3>
            <p>
              Every location page on every tenant site lists a discreet
              <em> "Other locals you'll love"</em> strip. SEO authority flows
              between you all, AI Overviews pick up the cluster, and customers
              discover their next favourite spot through your site.
            </p>
          </article>
          <article className="sp-community-card">
            <div className="sp-community-card__icon"><ShieldCheck size={20} /></div>
            <h3>Group buying power</h3>
            <p>
              Joint deals with our integration partners (DoorDash, Square,
              Resend, Cloudflare) at rates a single restaurant could never
              negotiate. We pass <b>100%</b> of the discount back.
            </p>
          </article>
        </div>

        <div className="sp-quote-card" data-testid="sales-community-quote">
          <p>
            <em>
              "We were neighbours for three years and never spoke. Now we
              share specials, refer each other's customers and run a joint
              Christmas campaign. Revenue's up 18% — and we made a friend."
            </em>
          </p>
          <footer>— Adrian, Bistro Aurora · Brisbane</footer>
        </div>
      </div>
    </section>
  );
}

export function ReviewsBlock() {
  return (
    <section className="sp-section sp-section--reviews" id="reviews" data-testid="sales-reviews-block">
      <div className="sp-container">
        <div className="sp-eyebrow sp-eyebrow--green">
          <Star size={14} /> Reviews on autopilot
        </div>
        <h2 className="sp-h2">
          You earned the 5 stars. We help you <em>keep them, share them, defend them</em>.
        </h2>
        <p className="sp-lede" style={{ maxWidth: 720 }}>
          Reviews are the single biggest driver of foot traffic into a local
          restaurant — and the single most exhausting thing for an owner to
          manage. HustlePlate runs the whole review lifecycle, end to end.
        </p>

        <div className="sp-review-grid">
          <article className="sp-review-card">
            <div className="sp-review-card__icon sp-review-card__icon--green"><ShieldCheck size={20} /></div>
            <h3>Control which reviews you publish</h3>
            <p>
              Approve, hide, or feature any review before it appears on your
              public site. Every star is moderated. Nothing goes live unless
              you say so.
            </p>
          </article>
          <article className="sp-review-card">
            <div className="sp-review-card__icon sp-review-card__icon--amber"><Share2 size={20} /></div>
            <h3>Auto-post 5-stars to socials</h3>
            <p>
              Every 5-star review can be auto-formatted into a branded
              social card (Instagram, Facebook, TikTok carousel) and posted
              with the customer's first name + your branding. Free user-
              generated content forever.
            </p>
          </article>
          <article className="sp-review-card">
            <div className="sp-review-card__icon sp-review-card__icon--rose"><MessageSquareReply size={20} /></div>
            <h3>AI auto-respond — in your voice</h3>
            <p>
              The platform writes a tone-matched reply to every Google,
              Facebook and direct review within 30 minutes. You approve once,
              we send forever. Negative reviews flagged for owner review first.
            </p>
          </article>
          <article className="sp-review-card">
            <div className="sp-review-card__icon sp-review-card__icon--blue"><Megaphone size={20} /></div>
            <h3>Reply to social comments too</h3>
            <p>
              Instagram comments, Facebook comments, TikTok replies — all
              triaged into one inbox. Auto-respond to FAQs ("are you open?",
              "do you do vegan?") so your inbox actually closes some days.
            </p>
          </article>
        </div>

        <div className="sp-stat-strip" data-testid="sales-reviews-stats">
          <div className="sp-stat-strip__item">
            <div className="sp-stat-strip__num">+37%</div>
            <div className="sp-stat-strip__label">average Google ranking lift in 60 days</div>
          </div>
          <div className="sp-stat-strip__item">
            <div className="sp-stat-strip__num">&lt;30 min</div>
            <div className="sp-stat-strip__label">AI auto-response time on every review</div>
          </div>
          <div className="sp-stat-strip__item">
            <div className="sp-stat-strip__num">100%</div>
            <div className="sp-stat-strip__label">of 5-stars eligible for branded social re-share</div>
          </div>
        </div>
      </div>
    </section>
  );
}


/* ─────────────────────────────────────────────────────────────────────
 *  CrossRedeemBlock — Hustle Credits work at EVERY claimed business.
 *  Nobody else offers this.
 *  Drop between <LoyaltyVouchersBlock/> and <CommunityBlock/>.
 * ───────────────────────────────────────────────────────────────────── */
export function CrossRedeemBlock() {
  return (
    <section className="sp-section sp-section--paper" id="cross-redeem" data-testid="sales-cross-redeem">
      <div className="sp-container">
        <div className="sp-eyebrow sp-eyebrow--amber">
          <Gift size={14} /> Bonus only HustlePlaza offers
        </div>
        <h2 className="sp-h2">
          Customers walk in with <em>credit they earned on Hustle</em> —
          you get the cash AND their contact details.
        </h2>
        <p className="sp-lede" style={{ maxWidth: 760 }}>
          Once a business claims its Hustle Directory listing, it joins the
          cross-redemption network. Customers spend Hustle Dollars they earned
          across the entire HustleVerse — at your register. You're reimbursed
          in cash by Hustle within 14 days, AND you get the customer added to
          your own CRM the moment they walk in via the HustleApp. New customer
          acquisition + payment, in one transaction. Nobody else does this.
        </p>

        <div className="sp-loyalty-grid" style={{ marginTop: 28 }}>
          <div className="sp-loyalty-card">
            <div className="sp-loyalty-card__icon"><Ticket size={20} /></div>
            <h3>Your own credit pot first</h3>
            <p>Customer redemptions use your business-specific credits first
              (refunds, loyalty rewards, promo grants). Only if those run out
              does the system reach into platform Hustle Dollars.</p>
          </div>
          <div className="sp-loyalty-card">
            <div className="sp-loyalty-card__icon"><Sparkles size={20} /></div>
            <h3>Cash reimbursement, net-14</h3>
            <p>Every dollar of platform credits redeemed at your venue lands
              in your bank account within 14 days — no chargebacks, no
              processing fees. Hustle takes the hit on bad customers.</p>
          </div>
          <div className="sp-loyalty-card">
            <div className="sp-loyalty-card__icon"><Users size={20} /></div>
            <h3>Free customer acquisition</h3>
            <p>The moment a customer arrives via HustleApp, their contact
              snapshot (name, email, phone) is auto-imported into your CRM.
              You'd pay $30-$80 to acquire this contact through Facebook ads
              — here it's bundled in.</p>
          </div>
          <div className="sp-loyalty-card">
            <div className="sp-loyalty-card__icon"><ShieldCheck size={20} /></div>
            <h3>Atomic + audit-trail</h3>
            <p>Every redemption is one atomic database transaction with a
              dual-ledger entry (one for your books, one for ours). Insufficient
              funds = clean error. Race conditions = impossible. Disputes are
              traceable to the exact bill reference.</p>
          </div>
        </div>

        <div className="sp-stat-strip" style={{ marginTop: 32 }}>
          <div className="sp-stat-strip__item">
            <div className="sp-stat-strip__num">$0</div>
            <div className="sp-stat-strip__label">cost to join the cross-redemption network</div>
          </div>
          <div className="sp-stat-strip__item">
            <div className="sp-stat-strip__num">14&nbsp;days</div>
            <div className="sp-stat-strip__label">to bank-deposit reimbursement</div>
          </div>
          <div className="sp-stat-strip__item">
            <div className="sp-stat-strip__num">100%</div>
            <div className="sp-stat-strip__label">of HustleApp arrivals captured into your CRM</div>
          </div>
        </div>
      </div>
    </section>
  );
}
